import { textColorFields } from '../_shared/textColor.js';
import { animationFields } from '../_shared/universal.js';
/**
 * horizontal-ticker — a full-width, horizontally SCROLLING marquee strip (a "ticker") of short repeating
 * messages, the chrome strip that often sits above the announcement bar / header. Up to 3 non-empty
 * messages are joined by a `separator` into a single track that scrolls endlessly via PURE CSS — no JS,
 * no client island (unlike the sibling announcement-bar, which rotates with a setInterval).
 *
 * COLOR mirrors announcement-bar exactly: a scheme select (accent / inverse / background / surface)
 * re-binds the `--aqx-scheme-*` contract via `data-scheme`, OR a `custom` pair of hex colors (resolved
 * from a stored ColorValue, same idiom as blocks/price `colorHex`). `surface` has no `[data-scheme]`
 * rule of its own, so it maps to the theme surface/ink vars directly while keeping the contract intact.
 *
 * VISIBILITY mirrors the block-level `visibility` select (always / mobile-only / desktop-only) and is
 * emitted as a scoped @media + @container(aqx-page) rule so it reflows in BOTH the live storefront and
 * the builder device-preview.
 */
export const horizontalTickerSchema = {
    type: 'horizontal-ticker',
    name: 'Horizontal ticker',
    version: 1,
    templates: ['index', 'collection', 'page', 'product'],
    fields: [
        // ── Messages (up to 3; only non-empty ones render, repeating across the strip) ──
        {
            id: 'message1',
            type: 'text',
            label: 'Message 1',
            group: 'Messages',
            default: 'Livraison offerte dès 400 MAD',
            defaultKey: 'section.ticker_free_shipping',
        },
        {
            id: 'message2',
            type: 'text',
            label: 'Message 2',
            group: 'Messages',
            default: 'Nouveautés chaque semaine',
            defaultKey: 'section.ticker_new_weekly',
        },
        {
            id: 'message3',
            type: 'text',
            label: 'Message 3',
            group: 'Messages',
            default: '',
        },
        // ── Appearance ───────────────────────────────────────────────────────────────
        {
            id: 'separator',
            aiWritable: false, // 0059 — machine format, not prose
            type: 'text',
            label: 'Separator',
            group: 'Appearance',
            info: 'Shown between repeated messages',
            default: '•',
        },
        {
            id: 'speed',
            type: 'range',
            label: 'Speed',
            group: 'Appearance',
            info: 'Seconds for one full loop — lower = faster',
            default: 40,
            min: 10,
            max: 120,
            step: 5,
            unit: 's',
        },
        {
            id: 'direction',
            type: 'select',
            label: 'Direction',
            group: 'Appearance',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'pauseOnHover',
            type: 'checkbox',
            label: 'Pause on hover',
            group: 'Appearance',
            default: true,
        },
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Appearance',
            default: 'inverse',
            options: [
                { value: 'accent', label: 'Accent' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'background', label: 'Background' },
                { value: 'surface', label: 'Surface' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customBg',
            type: 'color',
            label: 'Custom background',
            group: 'Appearance',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Custom text',
            group: 'Appearance',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'textSize',
            type: 'range',
            label: 'Text size',
            group: 'Appearance',
            default: 13,
            min: 11,
            max: 18,
            step: 1,
            unit: 'px',
        },
        // The air between two messages (24/09/2026). The separator sits in the middle of it. 100px by
        // default — the joined-string rendering before this gave a single space on each side of the
        // bullet, which read as one long sentence rather than a strip of distinct messages.
        {
            id: 'messageGap',
            type: 'range',
            label: 'Message spacing',
            group: 'Appearance',
            info: 'Space between two messages, the separator in the middle of it',
            default: 100,
            min: 0,
            max: 300,
            step: 10,
            unit: 'px',
        },
        // ── Visibility ─────────────────────────────────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Visibility',
            group: 'Visibility',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'desktop-hidden', label: 'Mobile only' },
                { value: 'mobile-hidden', label: 'Desktop only' },
            ],
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
};
export const horizontalTickerPresets = [
    {
        name: 'Horizontal Ticker',
        settings: {
            message3: '',
            separator: '•',
            speed: 40,
            direction: 'left',
            pauseOnHover: true,
            colorScheme: 'inverse',
            customBg: { ref: 'custom', value: '#121212' },
            customText: { ref: 'custom', value: '#ffffff' },
            textSize: 13,
            messageGap: 100,
            visibility: 'always-display',
        },
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const horizontalTickerMeta = {
    label: 'Horizontal Ticker',
    group: 'Layout',
    icon: 'layout',
};
//# sourceMappingURL=schema.js.map